import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { db, storage } from "../../base";
import { doc, deleteDoc, collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { ref, listAll, deleteObject } from "firebase/storage";
import { Button, Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

const AdminModal = () => {
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [email, setEmail] = useState('');
    const [adminSaveStats, setAdminSaveStats] = useState(() => {
        try {
            const val = localStorage.getItem('adminSaveStats');
            return val === null ? true : val === 'true';
        } catch (e) {
            return true;
        }
    });
    const { t } = useTranslation();

    const toggleAdminModal = () => {
        setShowAdminModal(!showAdminModal);
    };

    const [targetUserId, setTargetUserId] = useState('');

    // Delete all feedback documents for a given userId under AIDetectionsRatings/{userId}/Feedback
    const deleteFeedbackForUser = async (userId) => {
        if (!userId) {
            toast.error('Please provide a user id');
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ALL feedback entries for user ${userId}? This cannot be undone.`)) {
            return;
        }

        try {
            const feedbackRef = collection(db, 'AIDetectionsRatings', userId, 'Feedback');
            const snapshot = await getDocs(feedbackRef);

            if (snapshot.empty) {
                toast.info('No feedback documents found for this user.');
                return;
            }

            const deletePromises = [];
            snapshot.docs.forEach(d => {
                deletePromises.push(deleteDoc(d.ref));
            });

            await Promise.all(deletePromises);
            toast.success(`Deleted ${snapshot.docs.length} feedback entries for user ${userId}`);
        } catch (error) {
            console.error('Error deleting feedback for user:', error);
            toast.error('Error deleting feedback for user. Check console for details.');
        }
    };

    // Find all users by email and delete their projects
    const findUsersAndDeleteProjects = async () => {
        try {
            console.log("Searching for users with email:", email);
            const usersRef = collection(db, "users");
            const q = query(usersRef, where("email", "==", email));
            const userSnapshots = await getDocs(q);

            if (userSnapshots.empty) {
                toast.error("No users found with this email.");
                console.error("Users not found:", email);
                return;
            }

            console.log(`Found ${userSnapshots.docs.length} users with email ${email}`);

            for (const userDoc of userSnapshots.docs) {
                const userId = userDoc.id;
                const userData = userDoc.data();

                console.log("Processing user:", userId);

                // Retrieve all projects associated with userId
                const projectsRef = collection(db, "Projects");
                const projectsQuery = query(projectsRef, where("userId", "==", userId));
                const projectSnapshot = await getDocs(projectsQuery);

                if (projectSnapshot.empty) {
                    toast.info(`No projects found for user: ${userId}`);
                    console.warn("No projects found for user:", userId);
                } else {
                    const projectIds = projectSnapshot.docs.map(doc => doc.id);
                    console.log(`User ${userId} has ${projectIds.length} projects.`);

                    // Delete each project
                    for (const projectId of projectIds) {
                        console.log(`Deleting project document: ${projectId}`);
                        await deleteDoc(doc(db, "Projects", projectId));
                        console.log(`Project ${projectId} successfully deleted.`);
                        await deleteProjectImages(userId, projectId);
                        toast.success(`Deleted project ${projectId}`);
                    }
                }

                // Delete user's entire storage
                await deleteUserStorage(userId);

                // Remove projectIds field from user document if it exists
                if (userData.projectIds) {
                    console.log(`Removing projectIds field from user document ${userId}`);
                    await updateDoc(doc(db, "users", userId), { projectIds: [] });
                    console.log("User document updated.");
                    toast.info("Removed project references from user document.");
                }
            }

            toast.success("All projects, images, and references for all users with this email have been deleted.");

        } catch (error) {
            console.error("Error deleting user projects:", error);
            toast.error("Error deleting user projects.");
        }
    };

    // Delete all images related to a project
    const deleteProjectImages = async (userId, projectId) => {
        const folderPath = `usersProjectsImages/${userId}/canvasImages/`;
        const folderRef = ref(storage, folderPath);

        try {
            console.log(`Listing images for deletion in: ${folderPath}`);
            const { items, prefixes } = await listAll(folderRef);

            if (items.length === 0) {
                console.warn(`No images found for project: ${projectId}`);
            }

            // Delete individual images
            for (const fileRef of items) {
                console.log(`Deleting image: ${fileRef.fullPath}`);
                await deleteObject(fileRef);
                console.log(`Deleted image: ${fileRef.fullPath}`);
                toast.info(`Deleted image: ${fileRef.name}`);
            }

            // Recursively delete subfolders
            for (const prefix of prefixes) {
                console.log(`Deleting subfolder: ${prefix.fullPath}`);
                await deleteAllFilesInFolder(prefix.fullPath);
            }

        } catch (error) {
            console.error("Error deleting images:", error);
            toast.error("Error deleting project images.");
        }
    };

    // Recursively delete all files in a folder
    const deleteAllFilesInFolder = async (folderPath) => {
        const folderRef = ref(storage, folderPath);
        try {
            console.log(`Listing files in folder: ${folderPath}`);
            const { items, prefixes } = await listAll(folderRef);

            // Delete all files
            for (const fileRef of items) {
                console.log(`Deleting file: ${fileRef.fullPath}`);
                await deleteObject(fileRef);
                console.log(`Deleted file: ${fileRef.fullPath}`);
            }

            // Recursively delete all subfolders
            for (const prefix of prefixes) {
                await deleteAllFilesInFolder(prefix.fullPath);
            }

        } catch (error) {
            console.error("Error deleting folder:", folderPath, error);
        }
    };

    // Delete user's entire storage directory
    const deleteUserStorage = async (userId) => {
        const userStoragePath = `usersProjectsImages/${userId}/`;
        console.log(`Deleting all storage for user: ${userId}`);
        await deleteAllFilesInFolder(userStoragePath);
    };

    return (
        <>
            <Button onClick={toggleAdminModal} data-admin-trigger>{t('admin')}</Button>

            <Modal show={showAdminModal} onHide={toggleAdminModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('adminPanel')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="admin-save-stats-switch"
                                label={t('Save evaluation stats')}
                                checked={adminSaveStats}
                                onChange={(e) => {
                                    const newVal = e.target.checked;
                                    setAdminSaveStats(newVal);
                                    try { localStorage.setItem('adminSaveStats', newVal ? 'true' : 'false'); } catch (err) { console.warn(err); }
                                }}
                            />
                            <Form.Text className="text-muted">{t('Toggle whether evaluations performed by admins should be persisted')}</Form.Text>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>{t('Enter User Email')}</Form.Label>
                            <Form.Control 
                                type="email" 
                                placeholder="Enter user email" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                            />
                        </Form.Group>
                        <Button onClick={findUsersAndDeleteProjects} className="mt-3">
                            {t('Find and Delete User Projects')}
                        </Button>
                        
                        <hr />
                        <Form.Group className="mt-3">
                            <Form.Label>Delete Feedback for a specific user (by userId)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Paste user id (e.g. ky0ewj1gVLdF4ylqGxxRiorwBrh2)"
                                value={targetUserId}
                                onChange={(e) => setTargetUserId(e.target.value)}
                            />
                            <Form.Text className="text-muted">This will remove all documents under AIDetectionsRatings/{'{userId}'}/Feedback</Form.Text>
                        </Form.Group>
                        <Button variant="danger" className="mt-2" onClick={() => deleteFeedbackForUser(targetUserId)}>
                            Delete All Feedback for User
                        </Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={toggleAdminModal}>
                        {t('close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdminModal;
